import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Calendar, Download, Copy, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatEventDate } from "@/lib/utils";

function formatIcsDate(date) {
  return date.toISOString().replace(/[-:]|\.\d{3}/g, "");
}

function googleCalUrl(event) {
  // Parse event.date + event.time into start/end
  const start = new Date(event.date + "T19:00:00Z");
  // Attempt to parse event.time like "7:30 PM"
  const m = /^\s*(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/i.exec(event.time || "");
  if (m) {
    let h = parseInt(m[1], 10);
    const min = m[2] ? parseInt(m[2], 10) : 0;
    const ap = (m[3] || "").toUpperCase();
    if (ap === "PM" && h < 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    start.setUTCHours(h, min, 0, 0);
  }
  const end = new Date(start.getTime() + 3 * 60 * 60 * 1000);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title || "Offline & Available",
    dates: `${formatIcsDate(start)}/${formatIcsDate(end)}`,
    details: event.description || "",
    location: `${event.venue || ""}, ${event.address || ""}`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("checking");
  const [ticket, setTicket] = useState(null);
  const [copied, setCopied] = useState(false);
  const sessionId = sp.get("session_id");

  useEffect(() => {
    if (!sessionId) { nav("/"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          // Fetch ticket + event details for the save-the-date experience
          try {
            const { data: t } = await api.get(`/tickets/by-session/${sessionId}`);
            setTicket(t);
          } catch (err) {
            // Non-blocking — page still renders confirmation even if ticket lookup fails
            console.warn("Ticket lookup failed", err);
          }
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("failed"); return; }
      } catch (err) {
        console.warn("Payment status poll failed (will retry)", err);
      }
      if (++tries < 12) setTimeout(poll, 2000); else setStatus("timeout");
    };
    poll();
  }, [sessionId, nav]);

  const downloadIcs = async () => {
    if (!ticket?.ticket_id) return;
    try {
      const res = await api.get(`/tickets/${ticket.ticket_id}/ics`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `offline-and-available-${ticket.event?.title || "event"}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.warn("ICS download failed", err);
      toast.error("Couldn't download calendar file");
    }
  };

  const shareLink = ticket?.event ? `${window.location.origin}/events/${ticket.event.event_id}` : "";

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.warn("Clipboard copy failed", err);
      toast.error("Copy failed — grab it from the address bar");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: ticket?.event?.title || "Offline & Available",
          text: `Come join me at ${ticket?.event?.title}!`,
          url: shareLink,
        });
      } catch (err) {
        // User dismissed the share sheet — expected, do nothing
        if (err?.name !== "AbortError") console.warn("Native share failed", err);
      }
    } else {
      copyShare();
    }
  };

  return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
      {status === "checking" && <div className="text-neutral-500">Confirming your payment…</div>}

      {status === "paid" && (
        <>
          <CheckCircle2 size={64} className="mx-auto gold" strokeWidth={1.2} />
          <h1 className="font-heading text-4xl md:text-5xl mt-6">You're in.</h1>
          <p className="text-neutral-600 mt-4 leading-relaxed">
            {ticket?.event ? (
              <>Your seat at <em>{ticket.event.title}</em> is confirmed. We've emailed you the details and a digital ticket code.</>
            ) : (
              <>Your ticket is confirmed. We've emailed you the details and a digital ticket code.</>
            )}
          </p>

          {ticket?.event && (
            <div className="mt-8 card-elegant p-6 text-left" data-testid="save-the-date-card">
              <div className="text-eyebrow gold">Save the date</div>
              <div className="font-heading text-xl mt-2">{formatEventDate(ticket.event.date, { weekday: "long", month: "long", day: "numeric" })} · {ticket.event.time}</div>
              <div className="text-neutral-600 text-sm">{ticket.event.venue}</div>

              <div className="mt-6 grid gap-2">
                <a
                  href={googleCalUrl(ticket.event)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="add-google-calendar"
                >
                  <Button variant="outline" className="w-full rounded-full justify-start">
                    <Calendar size={14} className="mr-2" /> Add to Google Calendar
                  </Button>
                </a>
                <Button
                  onClick={downloadIcs}
                  variant="outline"
                  className="w-full rounded-full justify-start"
                  data-testid="download-ics"
                >
                  <Download size={14} className="mr-2" /> Add to Apple / Outlook (.ics)
                </Button>
                <Button
                  onClick={nativeShare}
                  className="w-full rounded-full bg-black text-white btn-pill justify-start"
                  data-testid="share-event"
                >
                  <Share2 size={14} className="mr-2" /> Invite a friend
                </Button>
                <button
                  onClick={copyShare}
                  className="text-xs text-neutral-500 hover:text-black mt-1 flex items-center justify-center gap-1"
                  data-testid="copy-event-link"
                >
                  <Copy size={12} /> {copied ? "Link copied" : "Copy link"}
                </button>
              </div>
            </div>
          )}

          <div className="mt-8 flex gap-3 justify-center">
            <Link to="/dashboard"><Button className="rounded-full bg-black text-white btn-pill" data-testid="ps-view-tickets">View my tickets</Button></Link>
            <Link to="/events"><Button variant="outline" className="rounded-full">Browse events</Button></Link>
          </div>
        </>
      )}

      {status === "failed" && (
        <><h1 className="font-heading text-4xl mt-6">Payment didn't go through.</h1><p className="text-neutral-600 mt-4">Please try again.</p></>
      )}
      {status === "timeout" && (
        <><h1 className="font-heading text-4xl mt-6">Still processing…</h1><p className="text-neutral-600 mt-4">Check your dashboard in a minute for confirmation.</p></>
      )}
    </div>
  );
}
